import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { useFeature } from '@/features/shared/hooks';
import { loadOwnPhoto } from '@/lib/appearance/own-photo';
import { useAuth } from '@/lib/auth/auth-provider';
import { ThemePicture } from './theme-picture';
import { useAppearance } from './use-appearance';

/**
 * The own photo as an object URL (cached on the device, see `own-photo.ts`). Without the photo
 * – never loaded, storage cleared, no Cache Storage – the theme's picture is shown: never a
 * broken image.
 */
function useOwnPhoto(url: string | null, wanted: boolean): string | null {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!wanted) {
      setObjectUrl(null);
      return;
    }
    let cancelled = false;
    let created: string | null = null;
    void loadOwnPhoto(url)
      .then((blob) => {
        if (!blob || cancelled) return;
        created = URL.createObjectURL(blob);
        setObjectUrl(created);
      })
      .catch(() => {
        if (!cancelled) setObjectUrl(null);
      });
    return () => {
      cancelled = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [url, wanted]);
  return wanted ? objectUrl : null;
}

/** Pictures are loaded for nobody who asked the browser to save data. */
function savesData(): boolean {
  const connection = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection;
  return (
    connection?.saveData === true ||
    (typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-data: reduce)').matches)
  );
}

/**
 * The one layer behind the app frame (appearance spec 6.3). Rendered by the app shell; cooking
 * mode and the scanner stay calm – palette only. Dialogs and sheets have surfaces of their own,
 * print hides the layer (stylesheet).
 */
export function AppBackdrop() {
  const { pathname } = useLocation();
  const { status, user } = useAuth();
  const { theme, backgroundStyle, backgroundDim } = useAppearance();
  const customAllowed = useFeature('custom_backgrounds');
  const calm = pathname.startsWith('/cook/') || pathname.startsWith('/catalog/scan');

  const wantsPhoto = backgroundStyle === 'CUSTOM' && customAllowed && status === 'authenticated';
  const photo = useOwnPhoto(user?.backgroundUrl ?? null, wantsPhoto && !calm);

  // What is shown: an own photo that is not there falls back to the theme's picture; nobody
  // who saves data gets a photo; a calm page gets the plain background.
  const shown = calm
    ? 'NONE'
    : backgroundStyle === 'CUSTOM'
      ? photo && !savesData()
        ? 'CUSTOM'
        : 'IMAGE'
      : backgroundStyle;

  useEffect(() => {
    document.documentElement.setAttribute('data-backdrop', shown);
  }, [shown]);

  return (
    <div className="app-backdrop" data-style={shown} data-testid="app-backdrop" aria-hidden>
      {shown === 'IMAGE' && <ThemePicture theme={theme} className="app-backdrop__picture" />}
      {shown === 'CUSTOM' && photo && (
        <>
          <div
            className="app-backdrop__photo"
            style={{ backgroundImage: `url("${photo}")` }}
            data-testid="app-backdrop-photo"
          />
          <div
            className="app-backdrop__scrim"
            style={{ ['--backdrop-dim' as string]: String(backgroundDim / 100) }}
          />
        </>
      )}
    </div>
  );
}
